import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Guard unsaved edits: block in-app navigation to another page (back button, links, Inky's
 * navigate) and ask the browser to confirm a refresh or tab close. The page decides what to
 * do with a blocked navigation through the returned blocker (`proceed` or `reset`).
 *
 * Needs a data router (createBrowserRouter in App.js, createMemoryRouter in tests).
 *
 * @param {function(): boolean} isDirty - Whether there are unsaved edits right now (read at navigation time).
 * @param {object} [options] - Options.
 * @param {function(string, string): boolean} [options.allowNavigation] - Return true to let a move
 *   from the first pathname to the second through without asking.
 * @returns {import('react-router-dom').Blocker} The router blocker.
 */
export default function useUnsavedChangesGuard(isDirty, { allowNavigation } = {}) {
  const allowRef = useRef(allowNavigation);
  allowRef.current = allowNavigation;

  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) => {
      if (currentLocation.pathname === nextLocation.pathname) return false;
      if (allowRef.current?.(currentLocation.pathname, nextLocation.pathname)) return false;
      return isDirty();
    },
    [isDirty]
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!isDirty()) return;
      event.preventDefault();
      // Older browsers need returnValue set to show the prompt.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  return blocker;
}
